import React from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createMemoryHistory } from '@tanstack/react-router'
import {
  DEFAULT_ENROLLMENT_PATH,
  createEnrollmentRouter,
  enrollmentRoutePrefixes,
  router
} from '@/router'
import { setWidgetPortalContainer, setWidgetRuntimeActive } from '@/shared/utils/widgetPortal'
import { queryClient } from './queryClient'
import { ensureGooglePlacesStyles, ensureWidgetStyles, isolateWidgetEvents } from './widgetStyles'

function getWidgetInitialEntry() {
  const { pathname, search, hostname } = window.location

  if (enrollmentRoutePrefixes.some(prefix => pathname.startsWith(prefix))) {
    return pathname + search
  }

  return [
    'enrollment.160digital.com',
    'partners-enrollment.160digital.com',
    'enrollment.160drivingacademy.com',
    'partners-enrollment.160drivingacademy.com'
  ].includes(hostname)
    ? '/'
    : DEFAULT_ENROLLMENT_PATH
}

function resolveMountContainer(container: HTMLElement, shouldUseShadowRoot: boolean) {
  let mountContainer = container

  if (!shouldUseShadowRoot) {
    ensureWidgetStyles(document.head)
    setWidgetPortalContainer(document.body)
    return mountContainer
  }

  const shadowRoot = container.shadowRoot ?? container.attachShadow({ mode: 'open' })
  ensureWidgetStyles(shadowRoot)
  ensureGooglePlacesStyles()

  const existingMount = shadowRoot.getElementById('enrollment-widget-mount')
  mountContainer =
    existingMount instanceof HTMLElement ? existingMount : document.createElement('div')

  if (!(existingMount instanceof HTMLElement)) {
    mountContainer.id = 'enrollment-widget-mount'
    shadowRoot.appendChild(mountContainer)
  }

  const existingPortal = shadowRoot.getElementById('enrollment-widget-portal')

  if (existingPortal instanceof HTMLElement) {
    setWidgetPortalContainer(existingPortal)
    isolateWidgetEvents(existingPortal)
  } else {
    const nextPortal = document.createElement('div')
    nextPortal.id = 'enrollment-widget-portal'
    shadowRoot.appendChild(nextPortal)
    setWidgetPortalContainer(nextPortal)
    isolateWidgetEvents(nextPortal)
  }

  isolateWidgetEvents(mountContainer)

  return mountContainer
}

export function renderEnrollmentApp() {
  const container = document.getElementById('app') || document.getElementById('root')

  if (!container) {
    return false
  }

  const isEmbeddedHost = container.id === 'app'
  const isStandaloneShell = container.id === 'root' && !document.getElementById('app')
  const isWidgetScript = Boolean(
    (document.currentScript instanceof HTMLScriptElement &&
      document.currentScript.src.includes('enrollment-widget')) ||
      document.querySelector('script[src*="enrollment-widget"]')
  )
  const isWidget =
    isEmbeddedHost || window.location.protocol === 'file:' || (isWidgetScript && !isStandaloneShell)

  setWidgetRuntimeActive(isWidget)
  document.body.classList.toggle('enrollment-widget-page', isWidget)
  document.body.classList.toggle('enrollment-standalone-page', isStandaloneShell && !isWidget)

  const shouldUseShadowRoot = isWidget && isEmbeddedHost
  const mountContainer = resolveMountContainer(container, shouldUseShadowRoot)
  mountContainer.classList.add('enrollment-widget-root')

  const activeRouter = isWidget
    ? createEnrollmentRouter(createMemoryHistory({ initialEntries: [getWidgetInitialEntry()] }))
    : router

  createRoot(mountContainer).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={activeRouter} />
      </QueryClientProvider>
    </React.StrictMode>
  )

  return true
}
